import type {
  Account,
  Allocation,
  ExpenseCategory,
  GlidePath,
  LifeEvent,
  Plan,
  StreamGrowth,
  TimePoint,
} from '../plan/model';
import {
  isReturnInput,
  type MatrixReturn,
  type ReturnInput,
  type ReturnInputLike,
  type ReturnSequence,
} from '../sampling/sampler';
import { defaultOrdering, type OrderingStrategy } from '../strategies/index';
import { NoTaxPolicy, type TaxPolicy } from '../tax/policy';
import { accountColumn, categoryColumn, Ledger, type LedgerRow } from './ledger';

/**
 * The deterministic core (PLAN.md guiding principle #2, core invariant). `simulatePath` is
 * a PURE, SEED-FREE function: `(plan, returnInput) -> ledger`. It contains NO randomness —
 * the sampler owns all of that — so it is unit-testable with hand-built inputs
 * ("returns are 0% every year, expense is X -> balance must be exactly Y").
 *
 * M2 implements the full annual pipeline (BACKEND.md §4.4) over PER-ACCOUNT balances, in the
 * strict contract order:
 *   income -> contributions(strategy) -> expenses -> withdrawals(strategy)
 *          -> taxes(TaxPolicy) -> apply returns -> record ledger row.
 * It desugars `LifeEvent`s and event-relative `TimePoint`s to absolute years, and wires the
 * `matrix` (allocation-weighted, per-asset) return path. The signature and the ledger-first
 * contract are stable for M3 (the sampler feeds it `ReturnInput[]`).
 *
 * Money model (so the conservation identity holds exactly, and the M0 exact tests are
 * unchanged): income and expenses are the household's external cash flows; their NET moves
 * the invested pool (`income - expenses` per year). The aggregate `contributions` /
 * `withdrawals` columns are reserved for explicit reallocations that net to zero in the
 * simple flow case (so the M0 identity `end = start + income - expenses + 0 - 0 + growth -
 * taxes` stays exact). The PER-ACCOUNT drill-down columns `acct.<id>.contribution` /
 * `acct.<id>.withdrawal` record WHICH account received the surplus / sourced the shortfall,
 * routed by the OrderingStrategy — that is the multi-account debuggability surface.
 */

export interface SimulateOptions {
  /** Tax policy applied each year. Defaults to NoTaxPolicy (zero tax) to keep the core
   *  runnable in hand-built scenarios. */
  readonly taxPolicy?: TaxPolicy;
  /** Account-ordering strategy (contribution / withdrawal order). Defaults to the
   *  pass-through `DefaultOrdering` (honours `plan.strategy`, else natural account order). */
  readonly ordering?: OrderingStrategy;
}

/** A small epsilon so float dust never re-triggers a draw or leaves a negative residue. */
const EPS = 1e-9;

/**
 * Run one deterministic path. `returns` is a `ReturnInputLike` — a bare per-year array (M0
 * form), or a structured `ReturnInput` (`fixed` | `sequence` | `matrix`). It must cover the
 * horizon (else this throws). `matrix` returns are per-asset and weighted by each account's
 * (possibly glide-path-resolved) allocation; `fixed` / `sequence` apply one rate to every
 * account (single-pool semantics).
 */
export function simulatePath(
  plan: Plan,
  returns: ReturnInputLike,
  options: SimulateOptions = {},
): Ledger {
  const horizon = plan.horizonYears;
  const taxPolicy = options.taxPolicy ?? new NoTaxPolicy();
  const ordering = options.ordering ?? defaultOrdering;

  // --- Preflight (once) ---------------------------------------------------
  // 1. Desugar: resolve every TimePoint to an absolute year and collect one-time flows.
  const resolved = desugarPlan(plan);
  // 2. Resolve returns into a per-(year, account) decimal return.
  const accountReturn = resolveAccountReturn(returns, horizon, plan);
  // 2b. ENRICHMENT: a realized-inflation accessor for CPI-indexed streams. The per-year inflation
  //     series rides on the ReturnInput (`RealizedInflationCarrier.inflation`); a bare array / a
  //     ReturnInput with no inflation → flat 0 (the documented fallback). Indexed by SIMULATED-year
  //     offset (calendar `year - startYear`); out-of-range / absent → 0.
  const cpiFor = realizedInflationAccessor(returns, plan.startYear);
  // 2c. ENRICHMENT: each account's dividend YIELD (explicit `dividendYield` wins; else derived from
  //     the plan-level per-asset-class `assumptions.dividendYields` allocation-weighted; else 0).
  const dividendYields = resolveDividendYields(plan);
  // 3. Allocate the ledger with the full column set (core + per-account + per-category).
  const categories = activeCategories(plan);
  const extraColumns = [
    ...plan.accounts.flatMap((a) => [
      accountColumn(a.id, 'balance'),
      accountColumn(a.id, 'contribution'),
      accountColumn(a.id, 'withdrawal'),
      accountColumn(a.id, 'growth'),
    ]),
    ...categories.map((c) => categoryColumn(c)),
  ];
  const ledger = new Ledger(horizon, extraColumns);

  // Per-account mutable balances (the source of truth; the pool is their sum).
  const balances = plan.accounts.map((a) => a.balance);
  // ENRICHMENT: per-account COST BASIS (book value) for average-basis realized-gain accounting on
  // withdrawal. Back-compat default: a missing `costBasis` is treated as EQUAL to the opening
  // balance (zero embedded gain), so a plan that never set `costBasis` realises ZERO capital gains
  // — byte-identical to pre-enrichment behaviour. `cash` carries basis === balance too (no gain).
  const costBases = plan.accounts.map((a) => a.costBasis ?? a.balance);

  for (let i = 0; i < horizon; i++) {
    const year = plan.startYear + i;
    const startBalance = sum(balances);

    // The realised-gain / remittance tallies for THIS year (ENRICHMENT). `source` accumulates into
    // them as it draws from taxable accounts; they feed the YearTaxInput + the new ledger columns.
    const realized: RealizedTally = {
      gainsSet: 0,
      gainsForeign: 0,
      foreignRemitted: 0,
      gainsLongTermUS: 0,
      shortfall: 0,
    };

    // M8: per-kind START-of-year balances (== prior calendar year-end) for the US RMD calculation.
    // Captured BEFORE any flow touches the accounts this year. Thai/NoTax callers ignore it.
    const priorYearEndBalances = balancesByKind(plan, balances);

    // 1. INCOME — sum active income streams (grown from each stream's start; cpi-indexed if asked).
    const income = sumStreams(resolved.incomes, year, cpiFor);

    // 1b. DIVIDENDS ACCRUED — each account's `dividendYield` × its START-of-year balance. Treated
    //     as a cash inflow into the household budget (so it offsets the year's outflows like income)
    //     AND reported to the tax policy. 0 for every account by default (back-compat).
    let dividends = 0;
    for (let a = 0; a < plan.accounts.length; a++) {
      dividends += (balances[a] as number) * (dividendYields[a] as number);
    }

    // 2. CONTRIBUTIONS (strategy) + 3. EXPENSES — compute the net household cash flow, then
    //    route a surplus into accounts (contribution order). Expenses include any event
    //    one-time outflow landing this year. (Order is observed: income is known, the
    //    surplus is what's left after expenses; we resolve expenses here to size it.)
    const expenseByCategory = sumExpensesByCategory(resolved.expenses, year, cpiFor);
    const oneTimeFlow = resolved.oneTimeByYear.get(year) ?? 0;
    // oneTimeFlow is signed (negative = outflow). Fold an outflow into expenses, an inflow
    // into income-side surplus.
    const expenses = totalOf(expenseByCategory) + Math.max(0, -oneTimeFlow);
    const extraInflow = Math.max(0, oneTimeFlow);

    // Dividends spend before the portfolio is touched (they are realised cash), so they reduce the
    // year's net draw. Back-compat: dividends === 0 → `net` is exactly the M2 value.
    const net = income + dividends + extraInflow - expenses;

    // Per-account contribution / withdrawal drill-down for THIS year.
    const contribByAccount = new Float64Array(plan.accounts.length);
    const withdrawByAccount = new Float64Array(plan.accounts.length);

    if (net > EPS) {
      // Surplus -> deploy into accounts in contribution order. A contribution RAISES cost basis
      // (new money is bought at par), so basis tracks the inflow.
      deploy(
        plan,
        ordering.contributionOrder(plan, year),
        net,
        balances,
        costBases,
        contribByAccount,
      );
    } else if (net < -EPS) {
      // 4. WITHDRAWALS (strategy) — cover the shortfall in withdrawal order; realise gains by sleeve
      //    and track foreign remittance; floor at 0 and record any unfunded shortfall.
      source(
        plan,
        ordering.withdrawalOrder(plan, year),
        -net,
        balances,
        costBases,
        withdrawByAccount,
        realized,
      );
    }

    // 5. TAXES — one TaxPolicy call; deduct result.total by sourcing it like a withdrawal.
    //    The simulator only ASSEMBLES the year's facts; ALL tax RULES live behind the policy
    //    (CLAUDE.md invariant). ENRICHMENT: `dividends` (accrued above), `capitalGains` (realised by
    //    sleeve on the withdrawals above), and `foreignIncomeRemitted` (the foreign sleeve's remitted
    //    proceeds) are now REAL values plumbed from the plan — they default to 0 for a plan with no
    //    dividend yield / no embedded basis, so NoTaxPolicy and salary-only plans are unchanged.
    const tax = taxPolicy.compute({
      year,
      assessableIncome: assessableIncome(resolved.incomes, year, cpiFor),
      dividends,
      capitalGains: {
        setListed: realized.gainsSet,
        foreign: realized.gainsForeign,
        // M8: realised US long-term gains (0 unless a `taxable_us` account is drawn). Additive — Thai
        // / NoTax policies ignore `longTermUS`, so Thailand + the M4 goldens are byte-identical.
        longTermUS: realized.gainsLongTermUS,
      },
      foreignIncomeRemitted: realized.foreignRemitted,
      withdrawalsByKind: withdrawalsByKind(plan, withdrawByAccount),
      ages: agesInYear(plan, year),
      contributionsByKind: contributionsByKind(plan, contribByAccount),
      // M8: prior year-end per-kind balances for the US RMD floor. Additive — Thai / NoTax policies
      // ignore `priorYearEndBalances`.
      priorYearEndBalances,
    });
    if (tax.total > EPS) {
      source(
        plan,
        ordering.withdrawalOrder(plan, year),
        tax.total,
        balances,
        costBases,
        withdrawByAccount,
        realized,
      );
    }

    // 6. APPLY RETURNS — credit each account balance * r_account(account, i). Growth raises the
    //    market value but NOT the cost basis (it is unrealised gain until sold).
    const growthByAccount = new Float64Array(plan.accounts.length);
    let growth = 0;
    for (let a = 0; a < plan.accounts.length; a++) {
      const r = accountReturn(i, a);
      const g = (balances[a] as number) * r;
      growthByAccount[a] = g;
      balances[a] = (balances[a] as number) + g;
      growth += g;
    }

    const endBalance = sum(balances);
    // ENRICHMENT: FLOOR-AT-ZERO + DEPLETED flag. `source` floored each drained account at 0 and
    // accumulated any unfunded amount into `realized.shortfall`, so the pool is already >= 0; the
    // depleted flag is set the first time a year could not be fully funded. A path counts as FAILED
    // from its first depleted year (drives successProbability — see results.ts).
    const depleted = realized.shortfall > EPS ? 1 : 0;

    // 7. RECORD ROW — core aggregates + ENRICHMENT columns + per-account + per-category drill-down.
    const row: LedgerRow = {
      period: year,
      startBalance,
      income,
      expenses,
      contributions: 0,
      withdrawals: 0,
      growth,
      taxes: tax.total,
      endBalance,
      dividends,
      realizedGainsSet: realized.gainsSet,
      realizedGainsForeign: realized.gainsForeign,
      foreignRemitted: realized.foreignRemitted,
      shortfall: realized.shortfall,
      depleted,
    };
    for (let a = 0; a < plan.accounts.length; a++) {
      const acct = plan.accounts[a] as Account;
      row[accountColumn(acct.id, 'balance')] = balances[a] as number;
      row[accountColumn(acct.id, 'contribution')] = contribByAccount[a] as number;
      row[accountColumn(acct.id, 'withdrawal')] = withdrawByAccount[a] as number;
      row[accountColumn(acct.id, 'growth')] = growthByAccount[a] as number;
    }
    for (const [cat, amount] of expenseByCategory) {
      row[categoryColumn(cat)] = amount;
    }
    ledger.setRow(i, row);
  }

  return ledger;
}

// ---------------------------------------------------------------------------
// Surplus deployment / shortfall sourcing across accounts (drill-down + cost-basis).
// ---------------------------------------------------------------------------

/**
 * The per-year ENRICHMENT tally `source` accumulates into as it draws from taxable accounts:
 * realised capital gains split by sleeve, foreign-sleeve proceeds remitted to Thailand, and any
 * unfunded shortfall after the portfolio is floored at 0. Fed into `YearTaxInput` + the new ledger
 * columns. (Mutated in place so a year's two `source` calls — withdrawals then taxes — share one
 * tally.)
 */
interface RealizedTally {
  /** Realised gains on SET-listed / `taxable_set` sleeves (exempt; tracked for drill-down). */
  gainsSet: number;
  /** Realised gains on the foreign (`taxable_foreign`) sleeve (taxed via remittance). */
  gainsForeign: number;
  /** Foreign-sleeve proceeds remitted to Thailand this year (Por.161/162). */
  foreignRemitted: number;
  /** Realised LONG-TERM gains on the US (`taxable_us`) sleeve (M8) — taxed at 0/15/20%. 0 unless a
   *  `taxable_us` account with an embedded gain is drawn (so Thailand/NoTax plans are unaffected). */
  gainsLongTermUS: number;
  /** Unfunded withdrawal amount after flooring the portfolio at 0 (positive magnitude). */
  shortfall: number;
}

/** Deploy `amount` into accounts in `order`, all into the first available account.
 *  M2 has no per-account contribution caps (M4 wires those), so the whole surplus lands in
 *  the highest-priority account. Records the per-account contribution AND raises that account's
 *  cost basis (new money is bought at par, so it carries zero embedded gain). */
function deploy(
  plan: Plan,
  order: readonly string[],
  amount: number,
  balances: number[],
  costBases: number[],
  contribByAccount: Float64Array,
): void {
  const idx = indexOf(plan, order[0]);
  if (idx < 0) {
    return;
  }
  balances[idx] = (balances[idx] as number) + amount;
  // ENRICHMENT: a contribution buys at par — its basis equals its cost, so embedded gain is
  // unchanged. (Back-compat: with no costBasis ever set, basis stays == balance throughout.)
  costBases[idx] = (costBases[idx] as number) + amount;
  contribByAccount[idx] = (contribByAccount[idx] as number) + amount;
}

/**
 * Source `amount` from accounts in `order`, draining each before moving to the next. ENRICHMENT:
 *  - AVERAGE-BASIS REALISED GAIN: a `take` from an account realises a proportional slice of its
 *    embedded gain, `gain = take · (1 − basis/balance)` (0 when basis >= balance, i.e. no gain or a
 *    loss — losses are not modelled as offsets in v1), and reduces the basis pro-rata
 *    (`basis -= take · basis/balance`). The gain is routed by the account's KIND: `taxable_set`
 *    (and any SET-listed sleeve) → `realized.gainsSet` (exempt); `taxable_foreign` →
 *    `realized.gainsForeign`, and the WHOLE foreign `take` (proceeds) → `realized.foreignRemitted`
 *    (the assessable remitted income — the policy taxes the gain portion). `rmf`/`ssf`/`thai_esg`/
 *    `pvd`/`cash` realise no taxable CG here (deferred / exempt / not a gain vehicle).
 *  - FLOOR-AT-ZERO: each account is drained only to 0 (never negative). Any amount that cannot be
 *    funded after exhausting `order` (and the fallback account) is recorded as
 *    `realized.shortfall` — the portfolio is floored at 0 and the year is flagged DEPLETED. This
 *    is the ONE intended behaviour change (M5 worst-first no longer goes to −1.56M; it floors at 0
 *    and reports the shortfall + depleted flag).
 * Records per-account withdrawals (the drill-down `acct.<id>.withdrawal`).
 *
 * Back-compat: for a plan whose taxable accounts never set `costBasis` (basis == balance), every
 * realised gain is 0, so `realized` stays all-zero except `shortfall`; and a cash-only plan that
 * always has the balance to cover its draw records no shortfall — byte-identical to M2 for the
 * funded case, and floored (vs negative) only in the previously-negative case.
 */
function source(
  plan: Plan,
  order: readonly string[],
  amount: number,
  balances: number[],
  costBases: number[],
  withdrawByAccount: Float64Array,
  realized: RealizedTally,
): void {
  let remaining = amount;
  for (const id of order) {
    if (remaining <= EPS) {
      break;
    }
    const idx = indexOf(plan, id);
    if (idx < 0) {
      continue;
    }
    const available = balances[idx] as number;
    const take = Math.min(Math.max(available, 0), remaining);
    if (take > 0) {
      realizeFrom(plan.accounts[idx] as Account, take, available, idx, costBases, realized);
      balances[idx] = available - take;
      withdrawByAccount[idx] = (withdrawByAccount[idx] as number) + take;
      remaining -= take;
    }
  }
  // ENRICHMENT FLOOR-AT-ZERO: anything still unfunded after exhausting `order` is a real shortfall.
  // The portfolio is NOT charged negative; the deficit is recorded so the year is flagged depleted
  // and the path is treated as failed from here (results.ts keys success off the depleted flag).
  if (remaining > EPS) {
    realized.shortfall += remaining;
  }
}

/**
 * Realise the capital-gain slice of a `take` from one account (average-basis) and route it by the
 * account's KIND into the year's `realized` tally; reduce the account's cost basis pro-rata. Pure
 * w.r.t. balances (the caller debits the balance); only mutates `costBases[idx]` + `realized`.
 */
function realizeFrom(
  account: Account,
  take: number,
  available: number,
  idx: number,
  costBases: number[],
  realized: RealizedTally,
): void {
  const basis = costBases[idx] as number;
  // Fraction of the position being sold; average-basis realised gain on that fraction.
  const fraction = available > 0 ? take / available : 0;
  const basisSold = basis * fraction;
  const gain = take - basisSold; // > 0 when the slice carries an embedded gain; <= 0 otherwise.
  costBases[idx] = basis - basisSold;
  switch (account.kind) {
    case 'taxable_set':
      // SET-listed sold on the exchange — gain is EXEMPT, but tracked for drill-down.
      if (gain > 0) {
        realized.gainsSet += gain;
      }
      break;
    case 'taxable_foreign':
      // Foreign sleeve — the WHOLE proceeds are remitted; the gain portion is the assessable income
      // the policy taxes (Por.161/162). Track both: the gain for `capitalGains.foreign`, the gain
      // (assessable, post-2024-earned slice) as the remitted amount. v1 remits the realised GAIN as
      // the assessable foreign income (return-of-capital is excluded — see TAX.md §5 grandfather).
      if (gain > 0) {
        realized.gainsForeign += gain;
        realized.foreignRemitted += gain;
      }
      break;
    case 'taxable_us':
      // US (M8) taxable brokerage — the realised average-basis gain is a LONG-TERM capital gain taxed
      // at the preferential 0/15/20% rates by UnitedStatesTaxPolicy. (Short-term gains would be
      // ordinary income; v1 treats a realised taxable-brokerage gain as long-term — a documented
      // simplification, since the engine carries no per-lot holding period.)
      if (gain > 0) {
        realized.gainsLongTermUS += gain;
      }
      break;
    default:
      // rmf / ssf / thai_esg / pvd / traditional / roth / cash — no taxable CG realised on withdrawal
      // here. The Thai tax-deferred vehicles drive clawback via `withdrawalsByKind`; the US
      // `traditional` sleeve is taxed as ORDINARY income via `withdrawalsByKind.traditional` (and is
      // RMD-floored / early-penalised by UnitedStatesTaxPolicy); `roth` qualified withdrawals are
      // tax-free; cash is principal. No CG realised here for any of them.
      break;
  }
}

function indexOf(plan: Plan, id: string | undefined): number {
  if (id === undefined) {
    return -1;
  }
  return plan.accounts.findIndex((a) => a.id === id);
}

// ---------------------------------------------------------------------------
// Desugaring: resolve TimePoints to absolute years; expand LifeEvents.
// ---------------------------------------------------------------------------

/** A stream whose start/end have been resolved to absolute calendar years, and whose growth has
 *  been normalised to the `StreamGrowth` union (legacy scalar `growth` → `{kind:'fixed', rate}`
 *  unless an explicit `growthMode` overrides it). */
interface ResolvedStream {
  readonly amount: number;
  /** Normalised growth mode (ENRICHMENT). `fixed` compounds nominally; `cpi` indexes off the
   *  simulated year's realized inflation (see `streamAmount`). */
  readonly growth: StreamGrowth;
  readonly startYear: number;
  readonly endYear: number | undefined;
  readonly category: ExpenseCategory;
  readonly assessable: boolean;
}

/** Normalise a stream's growth into the `StreamGrowth` union: an explicit `growthMode` wins; else
 *  the legacy scalar `growth` becomes `{kind:'fixed', rate: growth}` (back-compat default). */
function resolveGrowth(growthMode: StreamGrowth | undefined, growth: number): StreamGrowth {
  return growthMode ?? { kind: 'fixed', rate: growth };
}

interface ResolvedPlan {
  readonly incomes: readonly ResolvedStream[];
  readonly expenses: readonly ResolvedStream[];
  /** Net one-time cash flows keyed by calendar year (signed: negative = outflow). */
  readonly oneTimeByYear: ReadonlyMap<number, number>;
}

/**
 * Desugar the plan: resolve every `TimePoint` to an absolute year (event-relative points are
 * resolved against their event, plus the offset), and collect each `LifeEvent.oneTimeFlow`
 * into a per-year signed total. Stream amounts/growth are carried through verbatim.
 */
function desugarPlan(plan: Plan): ResolvedPlan {
  const events = plan.events;
  const incomes: ResolvedStream[] = plan.incomes.map((s) => ({
    amount: s.amount,
    growth: resolveGrowth(s.growthMode, s.growth),
    startYear: resolveTimePoint(s.start, events, plan.startYear),
    endYear: s.end === undefined ? undefined : resolveTimePoint(s.end, events, plan.startYear),
    category: 'other',
    assessable: s.assessable,
  }));
  const expenses: ResolvedStream[] = plan.expenses.map((s) => ({
    amount: s.amount,
    growth: resolveGrowth(s.growthMode, s.growth),
    startYear: resolveTimePoint(s.start, events, plan.startYear),
    endYear: s.end === undefined ? undefined : resolveTimePoint(s.end, events, plan.startYear),
    category: s.category,
    assessable: true,
  }));
  const oneTimeByYear = new Map<number, number>();
  for (const e of events) {
    if (e.oneTimeFlow !== undefined) {
      const y = resolveTimePoint(e.at, events, plan.startYear);
      oneTimeByYear.set(y, (oneTimeByYear.get(y) ?? 0) + e.oneTimeFlow);
    }
  }
  return { incomes, expenses, oneTimeByYear };
}

/**
 * Resolve a `TimePoint` to an absolute calendar year. `year` -> itself; `event` -> the
 * referenced event's resolved year + `offsetYears`. The plan's `superRefine` already
 * guarantees event references exist; a missing event here would be a programming error, so we
 * fail loudly rather than silently mis-time a stream.
 */
function resolveTimePoint(tp: TimePoint, events: readonly LifeEvent[], startYear: number): number {
  if (tp.kind === 'year') {
    return tp.year;
  }
  const event = events.find((e) => e.id === tp.eventId);
  if (event === undefined) {
    throw new Error(`simulatePath: TimePoint references unknown event "${tp.eventId}"`);
  }
  return resolveTimePoint(event.at, events, startYear) + tp.offsetYears;
}

// ---------------------------------------------------------------------------
// Stream summation (uses resolved absolute years).
// ---------------------------------------------------------------------------

/**
 * The cumulative INDEXING FACTOR a stream's `amount` is multiplied by in `year`. For `fixed`
 * growth this is the closed-form `(1+rate)^(year−startYear)` (unchanged from M2). For `cpi` it is
 * the running PRODUCT of the realized per-year multipliers `(1+cpi_y)·(1+realRate)` over the
 * simulated years from `startYear` to `year` (so a `cpi` stream with `realRate:0` holds constant
 * real purchasing power). `cpiFor(y)` returns the realized inflation for calendar year `y` (0 when
 * the run carries no inflation column — the documented flat fallback). ENRICHMENT.
 */
function indexFactor(s: ResolvedStream, year: number, cpiFor: (y: number) => number): number {
  const n = year - s.startYear;
  if (n <= 0) {
    return 1;
  }
  if (s.growth.kind === 'fixed') {
    return (1 + s.growth.rate) ** n;
  }
  // cpi: compound the realized inflation of each simulated year from startYear+1..year, plus the
  // optional constant real drift. (Year `startYear` is the base — amount applies as-is there.)
  const realRate = s.growth.realRate;
  let factor = 1;
  for (let y = s.startYear + 1; y <= year; y++) {
    factor *= (1 + cpiFor(y)) * (1 + realRate);
  }
  return factor;
}

/** Sum active streams' grown annual amounts for `year`. */
function sumStreams(
  streams: readonly ResolvedStream[],
  year: number,
  cpiFor: (y: number) => number,
): number {
  let total = 0;
  for (const s of streams) {
    total += streamAmount(s, year, cpiFor);
  }
  return total;
}

/** Sum assessable income streams for `year` (feeds YearTaxInput). */
function assessableIncome(
  streams: readonly ResolvedStream[],
  year: number,
  cpiFor: (y: number) => number,
): number {
  let total = 0;
  for (const s of streams) {
    if (s.assessable) {
      total += streamAmount(s, year, cpiFor);
    }
  }
  return total;
}

/** Sum active expense streams for `year`, grouped by category. */
function sumExpensesByCategory(
  streams: readonly ResolvedStream[],
  year: number,
  cpiFor: (y: number) => number,
): Map<ExpenseCategory, number> {
  const byCategory = new Map<ExpenseCategory, number>();
  for (const s of streams) {
    const amount = streamAmount(s, year, cpiFor);
    if (amount !== 0) {
      byCategory.set(s.category, (byCategory.get(s.category) ?? 0) + amount);
    }
  }
  return byCategory;
}

/** A single stream's grown amount for `year`, or 0 if it is not active that year. */
function streamAmount(s: ResolvedStream, year: number, cpiFor: (y: number) => number): number {
  if (year < s.startYear) {
    return 0;
  }
  if (s.endYear !== undefined && year > s.endYear) {
    return 0;
  }
  return s.amount * indexFactor(s, year, cpiFor);
}

/** The set of expense categories that ANY expense stream uses (for ledger column allocation). */
function activeCategories(plan: Plan): ExpenseCategory[] {
  const seen = new Set<ExpenseCategory>();
  const out: ExpenseCategory[] = [];
  for (const e of plan.expenses) {
    if (!seen.has(e.category)) {
      seen.add(e.category);
      out.push(e.category);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Return resolution: per-(year, account) decimal return.
// ---------------------------------------------------------------------------

/**
 * Resolve a `ReturnInputLike` into `(yearIndex, accountIndex) -> decimal return`. Validates
 * length up front. `fixed`/`sequence`/bare-array apply the same per-year rate to every
 * account (single-pool); `matrix` weights asset-class returns by each account's effective
 * allocation for that year (glide paths resolved per year).
 */
function resolveAccountReturn(
  returns: ReturnInputLike,
  horizon: number,
  plan: Plan,
): (yearIndex: number, accountIndex: number) => number {
  // Bare array (M0 form) -> sequence semantics.
  if (Array.isArray(returns)) {
    const series = checkLength(returns as ReturnSequence, horizon);
    return (i) => series[i] as number;
  }
  const input = returns as ReturnInput;
  if (isReturnInput(input)) {
    switch (input.kind) {
      case 'fixed':
        return () => input.rate;
      case 'sequence': {
        const series = checkLength(input.byYear, horizon);
        return (i) => series[i] as number;
      }
      case 'matrix':
        return matrixAccountReturn(input, horizon, plan);
    }
  }
  throw new Error('simulatePath: unrecognised ReturnInput');
}

/** Validate a per-year series covers the horizon and return it. */
function checkLength(byYear: readonly number[], horizon: number): readonly number[] {
  if (byYear.length < horizon) {
    throw new Error(
      `simulatePath: return sequence too short (need ${horizon}, got ${byYear.length})`,
    );
  }
  return byYear;
}

/**
 * Build the per-(year, account) return for a `matrix` input: each account's return is the
 * allocation-weighted average of the asset-class returns, `r = Σ_a (w_a/Σw)·byYear[y][a]`,
 * with the account's effective allocation for that year resolved from its static allocation
 * or its glide path. Pre-resolves each account's per-year weight vector so the per-year loop
 * is a cheap dot product.
 */
function matrixAccountReturn(
  matrix: MatrixReturn,
  horizon: number,
  plan: Plan,
): (yearIndex: number, accountIndex: number) => number {
  const { assetClasses, byYear } = matrix;
  if (byYear.length < horizon) {
    throw new Error(
      `simulatePath: matrix return too short (need ${horizon}, got ${byYear.length})`,
    );
  }
  for (let y = 0; y < horizon; y++) {
    const row = byYear[y] as readonly number[];
    if (row.length !== assetClasses.length) {
      throw new Error(
        `simulatePath: matrix row ${y} has ${row.length} entries, expected ${assetClasses.length}`,
      );
    }
  }
  const glideById = new Map(plan.glidePaths.map((g) => [g.id, g]));

  // Pre-compute each account's normalised weight vector for each year (over assetClasses).
  // weights[account][year] is a Float64Array aligned with assetClasses.
  const weights: Float64Array[][] = plan.accounts.map((acct) => {
    const perYear: Float64Array[] = [];
    for (let y = 0; y < horizon; y++) {
      const year = plan.startYear + y;
      const alloc = effectiveAllocation(acct, year, glideById);
      perYear.push(normalisedWeights(alloc, assetClasses));
    }
    return perYear;
  });

  return (yearIndex, accountIndex) => {
    const row = byYear[yearIndex] as readonly number[];
    const w = weights[accountIndex]?.[yearIndex] as Float64Array;
    let r = 0;
    for (let a = 0; a < assetClasses.length; a++) {
      r += (w[a] as number) * (row[a] as number);
    }
    return r;
  };
}

/**
 * The allocation an account uses in `year`: its static `allocation`, or the effective stop of
 * its glide path (the most-recent stop at-or-before the year; the first stop also applies
 * before its own `at`).
 */
function effectiveAllocation(
  account: Account,
  year: number,
  glideById: ReadonlyMap<string, GlidePath>,
): Allocation {
  if (account.allocation !== undefined) {
    return account.allocation;
  }
  // The plan's `.refine`/`superRefine` guarantee a resolvable glide path here.
  const glide = glideById.get(account.glidePathRef as string);
  if (glide === undefined) {
    throw new Error(
      `simulatePath: account "${account.id}" references unknown glidePathRef "${account.glidePathRef}"`,
    );
  }
  return effectiveGlideAllocation(glide, year);
}

/** The effective allocation of a glide path for `year`: most-recent stop at-or-before it; the
 *  first stop also applies before its own `at`. Stops are declared chronological. */
function effectiveGlideAllocation(glide: GlidePath, year: number): Allocation {
  let chosen = glide.stops[0] as GlidePath['stops'][number];
  for (const stop of glide.stops) {
    const stopYear = timePointYear(stop.at);
    if (stopYear <= year) {
      chosen = stop;
    } else {
      break;
    }
  }
  return chosen.allocation;
}

/** A glide-path stop's `at` must be an absolute year (event-relative glide stops are M4+). */
function timePointYear(tp: TimePoint): number {
  if (tp.kind === 'year') {
    return tp.year;
  }
  // Glide-path stops anchored to events are not supported in M2 (no event context here).
  throw new Error('simulatePath: glide-path stops must use absolute-year TimePoints in M2');
}

/** Normalise an allocation into a weight vector aligned with `assetClasses` (Σw = 1; if all
 *  weights are zero, returns all-zero so the account simply earns 0% — a deliberate, visible
 *  result rather than a divide-by-zero). */
function normalisedWeights(alloc: Allocation, assetClasses: readonly string[]): Float64Array {
  const raw = assetClasses.map((id) => alloc[id] ?? 0);
  const total = raw.reduce((s, w) => s + w, 0);
  const out = new Float64Array(assetClasses.length);
  if (total > 0) {
    for (let a = 0; a < assetClasses.length; a++) {
      out[a] = (raw[a] as number) / total;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// ENRICHMENT helpers: realized-inflation accessor + dividend-yield resolution.
// ---------------------------------------------------------------------------

/**
 * Build the per-calendar-year realized-inflation accessor for CPI-indexed streams. The inflation
 * series rides on the `ReturnInput` (`RealizedInflationCarrier.inflation`), aligned by SIMULATED
 * year index (calendar `year - startYear`). A bare array (M0 form) or a ReturnInput with no
 * inflation column → a flat-0 accessor (the documented fallback so `cpi` streams never throw). Out
 * of range → 0.
 */
function realizedInflationAccessor(
  returns: ReturnInputLike,
  startYear: number,
): (year: number) => number {
  let series: readonly number[] | undefined;
  if (!Array.isArray(returns) && isReturnInput(returns as ReturnInput)) {
    series = (returns as ReturnInput).inflation;
  }
  if (series === undefined) {
    return () => 0;
  }
  const s = series;
  return (year: number) => {
    const idx = year - startYear;
    if (idx < 0 || idx >= s.length) {
      return 0;
    }
    return s[idx] as number;
  };
}

/**
 * Resolve each account's effective dividend YIELD (decimal), aligned with `plan.accounts`:
 *  1. an account's explicit `dividendYield` wins;
 *  2. else, if the plan declares `assumptions.dividendYields` (per asset-class), the account's
 *     yield is the allocation-weighted average of those class yields over the account's effective
 *     allocation in `plan.startYear` (`Σ_a (w_a/Σw)·yields[assetClasses[a]]`, missing keys → 0);
 *  3. else 0 (full back-compat — no dividends accrue).
 *
 * NOTE (v1 simplification): the per-class default is evaluated at the START-YEAR allocation only,
 * not re-resolved per year for glide paths. A per-year dividend yield (glide-aware) is a documented
 * follow-up; the dominant case (static allocation, or explicit per-account yield) is exact. ENRICHMENT.
 */
function resolveDividendYields(plan: Plan): number[] {
  const classYields = plan.assumptions?.dividendYields;
  const glideById = new Map(plan.glidePaths.map((g) => [g.id, g]));
  return plan.accounts.map((acct) => {
    if (acct.dividendYield !== undefined) {
      return acct.dividendYield;
    }
    if (classYields === undefined) {
      return 0;
    }
    const alloc = effectiveAllocation(acct, plan.startYear, glideById);
    let weightSum = 0;
    let weighted = 0;
    for (const [classId, w] of Object.entries(alloc)) {
      weightSum += w;
      weighted += w * (classYields[classId] ?? 0);
    }
    return weightSum > 0 ? weighted / weightSum : 0;
  });
}

// ---------------------------------------------------------------------------
// Small helpers.
// ---------------------------------------------------------------------------

function sum(xs: readonly number[]): number {
  let total = 0;
  for (const x of xs) {
    total += x;
  }
  return total;
}

function totalOf(byCategory: ReadonlyMap<ExpenseCategory, number>): number {
  let total = 0;
  for (const v of byCategory.values()) {
    total += v;
  }
  return total;
}

/** Map per-account withdrawals to a `withdrawalsByKind` total (for YearTaxInput / RMF rules). */
function withdrawalsByKind(plan: Plan, withdrawByAccount: Float64Array): Record<string, number> {
  const byKind: Record<string, number> = {};
  for (let a = 0; a < plan.accounts.length; a++) {
    const w = withdrawByAccount[a] as number;
    if (w !== 0) {
      const kind = (plan.accounts[a] as Account).kind;
      byKind[kind] = (byKind[kind] ?? 0) + w;
    }
  }
  return byKind;
}

/** Map per-account contributions to a `contributionsByKind` total (drives RMF/SSF/Thai-ESG/PVD
 *  deductions in the ThailandTaxPolicy). */
function contributionsByKind(plan: Plan, contribByAccount: Float64Array): Record<string, number> {
  const byKind: Record<string, number> = {};
  for (let a = 0; a < plan.accounts.length; a++) {
    const c = contribByAccount[a] as number;
    if (c !== 0) {
      const kind = (plan.accounts[a] as Account).kind;
      byKind[kind] = (byKind[kind] ?? 0) + c;
    }
  }
  return byKind;
}

/** Ages of household members during `year`. */
function agesInYear(plan: Plan, year: number): number[] {
  return plan.household.people.map((p) => year - p.birthYear);
}

/** Sum the current per-account balances by account kind (M8) — used to pass the prior calendar
 *  year-end balance per kind into the US RMD calculation (`priorYearEndBalances.traditional`).
 *  Captured at the START of a simulated year (before flows), so it equals the prior year-end. */
function balancesByKind(plan: Plan, balances: readonly number[]): Record<string, number> {
  const byKind: Record<string, number> = {};
  for (let a = 0; a < plan.accounts.length; a++) {
    const kind = (plan.accounts[a] as Account).kind;
    byKind[kind] = (byKind[kind] ?? 0) + (balances[a] as number);
  }
  return byKind;
}
